import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { setSuccess } from "../../../Redux/AlertReducer";
import { useDispatch } from "react-redux";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createAbility, updateAbility } from "../../../config/acl/ability";

const AddEmployeeForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    email: "",
    photo: null,
    active: 1,
    password: "",
    password_confirmation: "",
    permissions: [], // Initialize permissions as an empty array
  });

  const [permissions, setPermissions] = useState([]);

  const getPermissions = async () => {
    try {
      const { result, response } = await Helper.Get({
        url: `${api_Routes.Permissions.View}`,
      });
      if (response) {
        setPermissions(response.data);
      }
    } catch (error) {
      console.error("Error fetching details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPermissions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        photo: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (errorMessage && name === "password") {
        if (value === formData.password_confirmation) setErrorMessage();
      }
      if (errorMessage && name === "password_confirmation") {
        if (value === formData.password) setErrorMessage();
      }
    }
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionChange = (e) => {
    const { checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...(prev.permissions || []), value]
        : prev.permissions.filter((id) => id !== value), // Use .filter directly
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if passwords match
    if (formData.password !== formData.password_confirmation) {
      setErrorMessage(
        "Passwords do not match! Please ensure both passwords are the same."
      );
      return;
    }

    setIsLoading(true);

    const formDataToSend = new FormData();
    for (let key in formData) {
      if (key === "photo") {
        if (formData.photo && typeof formData.photo !== "string") {
          formDataToSend.append("photo", formData.photo);
        }
      } else if (key === "permissions") {
        formData.permissions.forEach((permissionId) => {
          formDataToSend.append("permissions[]", permissionId);
        });
      } else if (formData[key] !== null) {
        formDataToSend.append(key, formData[key]);
      }
    }

    try {
      const { result, response, message } = await Helper.Post({
        url: api_Routes.Employees.Add,
        data: formDataToSend,
        isFormData: true,
      });

      setIsLoading(false);
      if (response) {
        dispatch(setSuccess(true));
        navigate("/Employees");
      }
    } catch (error) {
      console.error("An error occurred during form submission:", error);
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
        <TextField
          fullWidth
          margin="normal"
          label="Name"
          name="name"
          required
          autoComplete="off"
          value={formData.name}
          onChange={handleInputChange}
          variant="outlined"
        />
        <div className="mt-3 mb-1">
          <FormControl className="w-full">
            <InputLabel id="role-select-label">Role *</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={formData.role || null}
              required
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  ["role"]: e.target.value,
                }));
              }}
              label="Role"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value="admin">Owner</MenuItem>
              <MenuItem value="manager">Admin</MenuItem>
              <MenuItem value="accountant">Accountant</MenuItem>
              <MenuItem value="assistance">Assistance</MenuItem>
              <MenuItem value="watcher">Watcher</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
        <TextField
          fullWidth
          margin="normal"
          label="Email"
          name="email"
          required
          type="email"
          autoComplete="off"
          value={formData.email}
          onChange={handleInputChange}
          variant="outlined"
        />
        <TextField
          fullWidth
          margin="normal"
          label="Upload Image"
          name="file"
          type="file"
          InputLabelProps={{ shrink: true }}
          onChange={handleInputChange}
          variant="outlined"
          inputProps={{
            accept: "image/*",
          }}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-4">
        <div className="mt-3 mb-3">
          <FormControl className="w-full mt-4 mb-2" variant="outlined">
            <InputLabel htmlFor="outlined-adornment-password">
              Password
            </InputLabel>
            <OutlinedInput
              fullWidth
              id="outlined-adornment-password"
              type={showPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
              name="password"
              required
              autoComplete="off"
              value={formData.password}
              onChange={handleInputChange}
              variant="outlined"
            />
          </FormControl>
        </div>

        <div className="mt-4 mb-3">
          <FormControl className="w-full" variant="outlined">
            <InputLabel htmlFor="outlined-adornment-confirm-password">
              Confirm password
            </InputLabel>
            <OutlinedInput
              fullWidth
              margin="normal"
              id="outlined-adornment-confirm-password"
              type={showPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm password"
              name="password_confirmation"
              required
              autoComplete="off"
              value={formData.password_confirmation}
              onChange={handleInputChange}
              variant="outlined"
            />
          </FormControl>
        </div>
      </div>
      {errorMessage && (
        <div className="w-full text-start text-red-600">*{errorMessage}</div>
      )}

      <div className="flex items-center justify-between gap-x-3">
        <div className="flex items-center justify-start gap-x-3">
          <FormLabel sx={{ fontWeight: "bold" }} component="legend">
            Active:
          </FormLabel>
          <RadioGroup
            name="active"
            value={formData.active}
            onChange={handleRadioChange}
            row
          >
            <FormControlLabel value="1" control={<Radio />} label="Yes" />
            <FormControlLabel value="0" control={<Radio />} label="No" />
          </RadioGroup>
        </div>
      </div>

      <div className="mt-4">
        <FormLabel
          sx={{ fontWeight: "bold", textAlign: "left", width: "100%" }}
          component="legend"
        >
          Permissions:
        </FormLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {permissions?.map((permission) => (
            <FormControlLabel
              key={permission.id}
              control={
                <Checkbox
                  name="permissions[]"
                  value={permission.id.toString()}
                  checked={formData.permissions?.includes(
                    permission.id.toString()
                  )}
                  onChange={handlePermissionChange}
                />
              }
              label={permission.name}
            />
          ))}
        </div>
      </div>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || errorMessage}
        color="primary"
        sx={{
          display: isLoading ? "flex" : "block",
          marginTop: 2,
          width: "20%",
          marginX: "auto",
          fontWeight: "bold",
          justifyContent: "center",
        }}
      >
        {isLoading ? <CircularProgress size={24} color="primary" /> : "Submit"}
      </Button>
    </form>
  );
};

export default AddEmployeeForm;
